import React, { useRef, useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useOptimizedImage } from '@app/hooks/useOptimizedImage';
import Rating from '../advisor_avatar/rating/rating';
import TopIcons from './top_icons';
import classes from './classes.module.scss';
import AdvisorVideoPlayer from '../advisor_video_player';
import { advisorProfileAvatarTheme } from '../../config/themes';
import ExpandedText from '../../../common/helpers/expanded_text';
import { getReviewCount, isPrerenderMode } from '../../config/util';
import env from '../../config/variables';
import { isKA } from '../../../common/config/utils';

const { BADGES } = env;

const defaultAvatar = require('../header/img/home_placeholder.png');

const renderBadge = (advisorBadgeData) => {
  const backgroundColor = advisorBadgeData.color;
  return (
    <div className= { classes.badgeContainer } style={ { backgroundColor } } suppressHydrationWarning>
      <img
        className = { classes.ratedImg }
        src = { advisorBadgeData.icon_url }
        alt=""
        suppressHydrationWarning
      />
      <span className={ classes.badgeText } suppressHydrationWarning>{ advisorBadgeData.name }</span>
    </div>
  );
};

const renderNameSectionContent = ({ nickname, serviceTitle, loading }) => (
  <div className={ classes.nameSectionContainer } itemProp="author" itemScope itemType="http://schema.org/Person">
    <div id="product-name-for-ref-to" itemProp="name" className={ classes.nickNameContainer }>
      <ExpandedText
        customClasses={ { text: classes.nickNameText } }
        skeleton={ <Skeleton width={ 170 } /> }
        loading={ loading }
        ellipsis
        limit={ 2 }
        textStyle={ { lineHeight: 1.15 } }
      >
        { nickname }
      </ExpandedText>
    </div>
    <div id="product-name-for-ref-to" itemProp="name" className={ classes.serviceTitleContainer }>
      <ExpandedText
        customClasses={ { text: classes.serviceTitleText } }
        skeleton={ <Skeleton width={ 170 } /> }
        loading={ loading }
        ellipsis
        limit={ 2 }
        textStyle={ { lineHeight: 1.15 } }
      >
        { serviceTitle }
      </ExpandedText>
    </div>
  </div>
);

const renderNameSection = ({
  nickname, serviceTitle, likesCount, dislikesCount, rating, loading
}) => {
  const reviewCount = getReviewCount({ likesCount, dislikesCount });
  if (reviewCount > 0 && rating > 0) {
    return (
      <div itemProp="review" itemScope itemType="https://schema.org/Review">
        { renderNameSectionContent({ nickname, serviceTitle, loading }) }
      </div>
    );
  }
  return renderNameSectionContent({ nickname, serviceTitle, loading });
};

const renderBadges = (badges) => {
  if (!badges || !badges.length) return <div />;
  if (!BADGES || !BADGES.length) return <div />;
  const advisorBadge = badges.find(item => item !== 'tryout');
  if (!advisorBadge) return <div />;
  const advisorBadgeData = BADGES.find((badge) => advisorBadge === badge.id);
  return renderBadge(advisorBadgeData);
};

const getAdvisorStatusClassName = ({ loading, status }) => {
  if (isPrerenderMode() || loading || !status) return classes.indicatorSkeleton;
  return classes[`${ status }Indicator`];
};

const renderAdvisorStatus = ({ status, t, loading }) => (
  <div className={ classes.indicatorContainer }>
    <div className={ getAdvisorStatusClassName({ loading, status }) }>
      <span className={ classes.statusTitle } suppressHydrationWarning>{ t(`advisor_profile.status_label_${ status }`).toUpperCase()}</span>
    </div>
  </div>
);

const renderTopIcons = (
  id,
  favorite,
  loading,
  theme,
  availabilityNotification,
  status
) => (
  <TopIcons
    id = { id }
    favorite = { favorite }
    loader = { loading }
    theme={ theme }
    availabilityNotification={ availabilityNotification }
    advisorStatus={ status }
  />
);

const renderRating = (loading, rating, isNewAdvisor) => {
  if (loading) return null;
  return (
    <Rating
      className={ classes.ratingContainer }
      rating={ rating }
      isNewAdvisor={ isNewAdvisor }
    />
  );
};

const renderTopIconsContainer = (
  loading,
  rating,
  isNewAdvisor,
  id,
  favorite,
  theme,
  availabilityNotification,
  status,
  profileVideoUrl,
  profileVideoThumbUrl,
  showVideoPlayer = true
) => (
  <div className={ classes.topItemsContainer }>
    <div className={ classes.topItemsLeftContainer }>
      { renderRating(loading, rating, isNewAdvisor) }
      {isKA() && showVideoPlayer && (
        <>
          { profileVideoUrl && (
          <video style={ { position: 'absolute' } } width="0" height="0">
            <source src={ profileVideoUrl } type="video/mp4" />
            <track kind="captions" src="" label="No captions" default hidden />
          </video>
          )}
          <div className={ classes.playBtnContainerKAMobile }>
            <AdvisorVideoPlayer
              profileVideoUrl={ profileVideoUrl }
              profilePictureUrl={ profileVideoThumbUrl }
              profileVideoThumbUrl={ profileVideoThumbUrl }
              needTrackProfileVideoEvent
              advisorId={ id }
            />
          </div>
        </>
      )}
    </div>
    { renderTopIcons(
      id,
      favorite,
      loading,
      theme,
      availabilityNotification,
      status
    ) }
  </div>
);

function AdvisorProfileAvatar({
  profilePictureUrl,
  profileVideoUrl = null,
  nickname,
  serviceTitle,
  rating = null,
  id = null,
  loading = null,
  favorite,
  isNewAdvisor,
  profileVideoThumbUrl,
  badges = [],
  status,
  theme = advisorProfileAvatarTheme.default,
  t,
  likesCount = null,
  dislikesCount = null,
  availabilityNotification,
  profilePictureDetails = {},
  profileGifUrl = null,
  countryFlagUrl = null
}) {
  const videoPlayerRef = useRef();
  const [showGif, setShowGif] = useState(!!profileGifUrl);

  useEffect(() => {
    if (!profileGifUrl) return undefined;
    setShowGif(true);
    const timer = setTimeout(() => setShowGif(false), 5000);
    return () => clearTimeout(timer);
  }, [profileGifUrl]);

  const showVideoPlayer = !profileGifUrl || !showGif;

  const handleAvatarClick = () => {
    if (profileVideoUrl && videoPlayerRef.current) {
      videoPlayerRef.current.play();
    }
  };

  const renderVideoPlayer = () => {
    if (theme !== advisorProfileAvatarTheme.default || !showVideoPlayer) return null;
    return (
      <div className={ isKA() ? classes.playBtnContainerKA : null }>
        {profileVideoUrl && (
        <video style={ { position: 'absolute' } } width="0" height="0">
          <source src={ profileVideoUrl } type="video/mp4" />
          <track kind="captions" src="" label="No captions" default hidden />
        </video>
        )}
        <AdvisorVideoPlayer
          ref={ videoPlayerRef }
          profileVideoUrl={ profileVideoUrl }
          profilePictureUrl={ profileVideoThumbUrl }
          profileVideoThumbUrl={ profileVideoThumbUrl }
          needTrackProfileVideoEvent
          advisorId={ id }
        />
      </div>
    );
  };

  const advisorInfo = () => (
    <div className={ classes.advisorInfo }>
      { renderBadges(badges) }
      { renderNameSection({
        nickname, serviceTitle, likesCount, dislikesCount, rating, loading
      }) }
      { countryFlagUrl && <img src={ countryFlagUrl } alt="country flag" className={ classes.advisorCountryFlag } /> }
    </div>
  );

  const optimizedUrl = useOptimizedImage({
    imageUrl: profilePictureUrl,
    imageDetails: profilePictureDetails,
    desktopSize: [280, 280], // Actual size is 382x382, but we need to scale it down to 280x280
    enableCaching: true // Enable image caching
  });

  const finalImageUrl = () => optimizedUrl || profilePictureUrl || defaultAvatar;

  const renderProfileAvatar = () => (
    <div className = { classes.profileContainer }>
      { profileGifUrl && showGif && (
      <div className={ classes.gifContainer }>
        <img src={ profileGifUrl } alt="Play" className={ classes.gifViewer } />
      </div>
      )}
      <div className={ classes.avatarContainer }>
        { renderTopIconsContainer(
          loading,
          rating,
          isNewAdvisor,
          id,
          favorite,
          theme,
          availabilityNotification,
          status,
          profileVideoUrl,
          profileVideoThumbUrl,
          showVideoPlayer
        ) }
        <div className = { classes.profileImageContainer }>
          <div className={ classes.transparentOverlay } />
          <button
            type="button"
            className={ classes.profileImgButton }
            style={ { cursor: profileVideoUrl && showVideoPlayer ? 'pointer' : 'default' } }
            onClick={ handleAvatarClick }
          >
            <img
              suppressHydrationWarning
              src={ finalImageUrl() }
              itemScope
              itemProp="image"
              className={ classes.profileImg }
              alt={ nickname }
              style={ !profilePictureUrl ? { objectFit: 'scale-down' } : null }
            />
          </button>
          { renderVideoPlayer() }
          { advisorInfo() }
        </div>
        <div className={ classes.statusContainer }>
          { renderAdvisorStatus({ status, t, loading }) }
        </div>
      </div>
    </div>
  );

  return (
    <div className={ classes[theme] }>
      {renderProfileAvatar()}
    </div>
  );
}

export default AdvisorProfileAvatar;
